/**
 * localStorage draft backstop for the LR Direct dispatch builder.
 *
 * The builder form lives in local React state; a reload / crash / accidental
 * navigation used to lose everything. We persist a lightweight snapshot per
 * load and restore it ONLY when the draft is newer than the dispatch row's
 * `updated_at`, so a stale draft can never resurrect over newer saved data.
 * Cleared on every successful save and on explicit discard.
 */

export interface DispatchDraftSnapshot {
  version: 1;
  savedAt: string; // ISO
  pallets: string;
  weight: string;
  commodity: string;
  notes: string;
  stops: any[];
}

const PREFIX = "lr_direct_dispatch_draft_v1:";

export function dispatchDraftKey(loadId: string): string {
  return `${PREFIX}${loadId}`;
}

export function writeDispatchDraft(
  loadId: string,
  snapshot: Omit<DispatchDraftSnapshot, "version" | "savedAt">,
): void {
  try {
    const payload: DispatchDraftSnapshot = { version: 1, savedAt: new Date().toISOString(), ...snapshot };
    localStorage.setItem(dispatchDraftKey(loadId), JSON.stringify(payload));
  } catch { /* quota / private mode — non-fatal */ }
}

export function readDispatchDraft(loadId: string): DispatchDraftSnapshot | null {
  try {
    const raw = localStorage.getItem(dispatchDraftKey(loadId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== 1 || !parsed.savedAt) return null;
    return parsed as DispatchDraftSnapshot;
  } catch {
    return null;
  }
}

export function clearDispatchDraft(loadId: string): void {
  try { localStorage.removeItem(dispatchDraftKey(loadId)); } catch { /* noop */ }
}

/**
 * A draft is only safe to restore when it was written AFTER the last server-side
 * save of the dispatch. `dispatchUpdatedAt` null (no dispatch row yet) means any
 * draft is newer by definition.
 */
export function isDraftNewerThan(draft: DispatchDraftSnapshot, dispatchUpdatedAt: string | null | undefined): boolean {
  const draftMs = Date.parse(draft.savedAt);
  if (Number.isNaN(draftMs)) return false;
  if (!dispatchUpdatedAt) return true;
  const savedMs = Date.parse(dispatchUpdatedAt);
  if (Number.isNaN(savedMs)) return true;
  return draftMs > savedMs;
}
